'use client';
import React from 'react';
import { Expression } from '@benzinga/session';
import { SearchBar, TokenizedExpression, SearchTool } from '../SearchBar';
import styled from '@benzinga/themetron';
import { parseExpression, toExpression, TokenData } from './tokenize';
import { PermissionPrompt } from '@benzinga/user-context';
import { SearchItem } from '@benzinga/search-modules';
import { ExpressionSearchModule, KeywordTag } from './Modules';
import { TooltipButton } from '@benzinga/widget-tools';
import { ExpressionBuilder } from '../ExpressionBuilder';
import { SmallSearch, Lock } from '@benzinga/themed-icons';
import { noop } from '@benzinga/utils';

export interface ExpressionSearchProps<T extends string> {
  className?: string;
  clear?: () => void;
  displayOr?: boolean;
  disableWrappedSearchbar?: boolean;
  excludeAnd?: boolean;
  expression: Expression<T>;
  flightMode?: boolean;
  iconOverride?: React.ReactNode;
  initExpandedMode?: boolean;
  isFullScreenStyles?: boolean;
  placeholder: string;
  thinUI?: boolean;
  expressionBuilderActive?: boolean;
  onExpressionBuilderClose?: () => void;
  onDropDownStateChange?: (isOpen: boolean) => void;
  onExpressionUpdate: (expression: Expression<T>) => void;
  tools?: SearchTool[];
  modules: ExpressionSearchModule[];
  newsConfigSelector?: React.ElementType | null;
}

export const ExpressionSearch = function <
  T extends ExpressionSearchProps<E>,
  E extends string = T extends ExpressionSearchProps<infer E1> ? E1 : never,
>(props: T): React.ReactElement {
  const [showUpSellModal, setShowUpSellModal] = React.useState(false);

  const onToggleModal = (show: boolean) => {
    setShowUpSellModal(show);
  };

  const genAutocomplete = React.useCallback(
    (token: TokenData<E>): SearchItem | undefined => {
      let moduleItem: SearchItem | undefined = undefined;

      props.modules.some(module => {
        const item = module.expression.fromTokenData(token);
        if (!item) return false;
        moduleItem = item;
        return true;
      });

      return moduleItem;
    },
    [props.modules],
  );

  const expression = React.useMemo(
    () => parseExpression(props.expression, genAutocomplete, props.displayOr),
    [genAutocomplete, props.displayOr, props.expression],
  );

  const expressionWithOr = React.useMemo(
    () => parseExpression(props.expression, genAutocomplete, true),
    [genAutocomplete, props.expression],
  );

  const getExpressionType = React.useCallback((expression: TokenData<E>) => {
    switch (expression.leftOperand) {
      case 'Tickers.name':
        if (typeof expression.rightOperand === 'object' && !Array.isArray(expression.rightOperand)) {
          return 'object';
        } else {
          return 'array';
        }
      default:
        return 'phrase';
    }
  }, []);

  const onExpressionUpdate = React.useCallback(
    (tags: TokenizedExpression[]) => {
      const tokens = tags.flatMap<TokenizedExpression<TokenData<E>>>(tag => {
        if (tag.operator !== 'expression') return tag;
        let convertedToken: TokenData<E> | undefined = undefined;

        for (const module of props.modules) {
          const token = module.expression.toTokenData(tag) as TokenData<E>;
          if (token) {
            convertedToken = token;
            break;
          }
        }
        return convertedToken ? [{ ...tag, data: convertedToken }] : [];
      });
      const expression = toExpression(tokens, getExpressionType);
      const onExpressionUpdate = props.onExpressionUpdate;
      onExpressionUpdate(expression);
    },
    [getExpressionType, props.modules, props.onExpressionUpdate],
  );

  const onLockedItemsSelected = React.useCallback(() => {
    setShowUpSellModal(true);
  }, []);

  const SearchDiv = props.thinUI ? ThinExpressionSearchStyler : ExpressionSearchDiv;

  const iconOverride = React.useMemo(
    () =>
      props.iconOverride ?? (
        <TooltipButton className={`TUTORIAL_WidgetContainer-Settings`}>
          <PaddingDiv>
            <SmallSearch />
          </PaddingDiv>
        </TooltipButton>
      ),
    [props.iconOverride],
  );

  return (
    <SearchDiv className={props.className}>
      {showUpSellModal && <PermissionPrompt onClose={onToggleModal} showUpsell />}
      <ExpressionBuilder
        expression={expressionWithOr}
        modules={props.modules}
        onClose={props.onExpressionBuilderClose ?? noop}
        onExpressionUpdate={onExpressionUpdate}
        visible={props.expressionBuilderActive ?? false}
      />
      <SearchBar
        displayOr={props.displayOr}
        excludeAnd={props.excludeAnd}
        expression={expression}
        flightMode={props.flightMode}
        iconOverride={iconOverride}
        initExpandedMode={props.initExpandedMode}
        modules={props.modules}
        newsConfigSelector={props?.newsConfigSelector}
        onDropDownStateChange={props.onDropDownStateChange}
        onExpressionUpdate={onExpressionUpdate}
        onLockedItemsSelected={onLockedItemsSelected}
        placeholder={props.placeholder}
        thinUI={props.thinUI}
        tools={props.tools}
      />
    </SearchDiv>
  );
};

const StyledLinkLinking = styled.div`
  margin: 8px;
  margin-right: 0px;
`;

export const ExpressionSearchDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  width: 100%;
  max-width: 100%; //search input max width to enable scroll in firefox;

  align-items: center;

  position: relative;
  border: 0.01em solid ${props => props.theme.colors.borderDark};
`;

export const ExpressionResultsQuotes = styled.div`
  display: flex;
  margin: 3px 0.75em;
`;

export const ExpressionResultsPrice = styled.span`
  margin: 0 !important;
`;

export const ExpressionResultsNominalChange = styled.span`
  margin: 0 8px;
`;

export const ResultHead = styled.div<{ $isSelected: boolean }>`
  font-size: 0.9em;
  color: ${props => (props.$isSelected ? props.theme.colors.foregroundActive : props.theme.colors.foregroundInactive)};
`;

export const ResultDescription = styled.div<{ $isSelected: boolean }>`
  text-overflow: ellipsis;
  color: ${props => (props.$isSelected ? props.theme.colors.foregroundActive : props.theme.colors.foreground)};
`;

export const ExpressionLocked = styled(Lock)`
  fill: ${props => props.theme.colors.accent};
`;

const ThinExpressionSearchStyler = styled(ExpressionSearchDiv)`
  margin-right: 5px;
  /* min-width: 250px; */
  background-color: ${props => props.theme.colors.background};

  ${ExpressionSearchDiv} {
    margin: 2px;
    border: 0.01em solid ${props => props.theme.colors.borderDark};
    background-color: ${props => props.theme.colors.background};
    min-width: auto;
    max-height: 28px;
  }
  ${KeywordTag} {
    margin: 0;
    height: 16px;
    padding: 0px 4px;
  }

  ${StyledLinkLinking} {
    margin: 2px;
  }
  ${ResultDescription} {
    overflow: hidden;
    max-width: 100%;
  }
`;

const PaddingDiv = styled.div`
  padding: 0 8px;
  ${SmallSearch} {
    fill: ${props => props.theme.colors.accent};
  }
`;
